/**
 * Review-cycle boundary — the single source of truth for "is it Mid-Year or
 * Year-End right now" on the frontend.
 *
 * The boundary is the calendar date each year on which the YEAREND (H2) period
 * begins; MIDYEAR (H1) runs from Jan 1 up to the day before. It is HR-configurable
 * (performance.review_cycle_config, edited via the admin UI) so the Mid-Year →
 * Year-End cutover can be shifted without a code change. Mirrors the backend
 * resolver in performance-service `utils/review_period_utils.py`.
 *
 * Use {@link useReviewCycleConfig} to fetch the live config, then pass it here.
 */

export interface ReviewCycleConfig {
  h2_start_month: number // 1-12, the month YEAREND begins
  h2_start_day: number // 1-31, the day YEAREND begins
}

/** Fallback when the config hasn't loaded / the API is unavailable: 1 August. */
export const DEFAULT_REVIEW_CYCLE_CONFIG: ReviewCycleConfig = { h2_start_month: 8, h2_start_day: 1 }

/**
 * 'MIDYEAR' or 'YEAREND' for `date`, per the configured YEAREND start.
 * Uses the LOCAL date (what the user sees in their timezone).
 */
export function getCurrentReviewPeriod(
  date: Date = new Date(),
  cfg: ReviewCycleConfig = DEFAULT_REVIEW_CYCLE_CONFIG,
): 'MIDYEAR' | 'YEAREND' {
  const month = date.getMonth() + 1 // getMonth() is 0-indexed; config is 1-indexed
  const day = date.getDate()
  if (month < cfg.h2_start_month || (month === cfg.h2_start_month && day < cfg.h2_start_day)) {
    return 'MIDYEAR'
  }
  return 'YEAREND'
}

/** Lowercase 'mid-year' | 'year-end' variant (calibration components use this form). */
export function getCurrentCalibrationPeriod(
  date: Date = new Date(),
  cfg: ReviewCycleConfig = DEFAULT_REVIEW_CYCLE_CONFIG,
): 'mid-year' | 'year-end' {
  return getCurrentReviewPeriod(date, cfg) === 'MIDYEAR' ? 'mid-year' : 'year-end'
}
